import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import NECSLogo from '../components/NECSLogo';
import { Eye, EyeOff, ChevronDown, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { biometricsAvailable, loginWithBiometric } from '../lib/biometric';
import { loadAppleConfig, appleSignIn, AppleGlyph } from '../lib/appleSignIn';
import { safeSupportReturn, rememberSupportReturn, consumeSupportReturn } from '../lib/supportHandoffReturn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Field Staff sign-in: black-and-gold layout with the worker badge (same model as the Resident app).
  const staffMode = location.pathname === '/staff-login' || new URLSearchParams(location.search).get('next') === '/staff-app' || new URLSearchParams(location.search).get('app') === 'staff';
  const { setUser, checkAuth } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registerMenuOpen, setRegisterMenuOpen] = useState(false);
  // Iter-109 · Auth rate-limit meter + 2FA challenge step.
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [lockoutMsg, setLockoutMsg] = useState(null);
  const [twoFA, setTwoFA] = useState(null); // { pending_token }
  const [twoFACode, setTwoFACode] = useState('');

  // Priority routing list — CEOs / support get the full Executive HQ view.
  const NECS_PRIORITY_EMAILS = ['jkpay@nobleempirecs.com', 'support@nobleempirecs.com'];
  const loginDestination = user => {
    const direct = safeSupportReturn(new URLSearchParams(location.search).get('next'));
    const stored = consumeSupportReturn();
    return direct || stored || routeForUser(user);
  };

  // Which dashboard should we send the freshly-authenticated user to?
  const routeForUser = (user) => {
    const executiveRoles = ['admin', 'company_manager'];
    const staffDashboardRoles = [
      'manager', 'regional_manager', 'supervisor', 'crew_leader', 'agent', 'site_agent', 'staff',
    ];
    const emailLc = (user?.email || '').toLowerCase();
    const isCompanyDomain = emailLc.endsWith('@nobleempirecs.com') || emailLc.endsWith('@noble-empire.com');
    if (user.role === 'property_manager') return '/property-manager-dashboard';
    if (user.role === 'resident') return '/resident';
    if (executiveRoles.includes(user.role) || NECS_PRIORITY_EMAILS.includes(emailLc)) return '/company-manager-dashboard';
    if (staffDashboardRoles.includes(user.role) || isCompanyDomain) return '/staff-dashboard';
    return '/tenant-dashboard';
  };

  const [apple, setApple] = useState({ configured: false, client_id: null, ready: false });
  useEffect(() => { if (staffMode) loadAppleConfig().then(setApple).catch(() => {}); }, [staffMode]);
  const handleAppleLogin = async () => {
    if (!apple.configured) { toast.error('Apple Sign-In is not configured yet — NECS admin must add the Apple Services ID.'); return; }
    setLoading(true);
    try {
      const data = await appleSignIn({ clientId: apple.client_id, app: 'staff', redirectPath: '/staff-login' });
      setUser(data.user);
      try { await checkAuth(); } catch (_) { /* non-blocking */ }
      toast.success(`Welcome back, ${data.user.first_name || data.user.name || ''}`);
      navigate(loginDestination(data.user), { state: { user: data.user } });
    } catch (e) { if (e?.error !== 'popup_closed_by_user') toast.error(e.message || 'Apple sign-in failed.'); }
    finally { setLoading(false); }
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      const data = await loginWithBiometric(formData.email || '');
      setUser(data.user);
      try { await checkAuth(); } catch (_) { /* non-blocking */ }
      toast.success(`Welcome back, ${data.user.first_name || data.user.name || ''}`);
      navigate(loginDestination(data.user), { state: { user: data.user } });
    } catch (e) {
      toast.error(e.message || 'Biometric sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    rememberSupportReturn(new URLSearchParams(location.search).get('next'));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // After Google auth, the URL fragment #session_id=... triggers AuthCallback,
    // which then routes the user to the right dashboard based on their role.
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Detect in-app webview (Emergent preview, Facebook, Instagram, etc.) — Google blocks OAuth there
  const isEmbeddedWebView = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || '').toLowerCase();
    return /(wv|fban|fbav|instagram|line|; webview|emergent)/.test(ua) ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches && /android|iphone/.test(ua) && !/safari|chrome\//.test(ua));
  })();

  // Iter-109 · Shared post-login finisher — routes to the right dashboard
  // and surfaces the 180-day password-age rotation nudge for admins.
  const finishLogin = async (data) => {
    setUser(data.user);
    // Iter-178 fix — hydrate the AuthContext from /me so the user object
    // carries the enriched fields (property_code, pm_sub_role, etc.)
    // that IdentityBadge + sidebar gating rely on.
    try { await checkAuth(); } catch (_) { /* non-blocking */ }
    toast.success('Welcome!');
    if (data.password_rotation_due) {
      toast.warning(
        `Your password is ${data.password_age_days} days old. Please rotate it soon in Profile → Set / Change Password.`,
        { duration: 10000 }
      );
    }
    if (typeof data.backup_codes_remaining === 'number') {
      toast.info(`Backup code used — ${data.backup_codes_remaining} remaining. Regenerate them in Profile → 2FA.`, { duration: 8000 });
    }
    navigate(loginDestination(data.user), { state: { user: data.user } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = data.detail;
        if (detail && typeof detail === 'object') {
          if (detail.locked) {
            setLockoutMsg(detail.message || 'Too many failed attempts — sign-in is temporarily locked.');
            setAttemptsRemaining(null);
          } else if (typeof detail.attempts_remaining === 'number') {
            setAttemptsRemaining(detail.attempts_remaining);
            setLockoutMsg(null);
          }
          throw new Error(detail.message || 'Login failed');
        }
        throw new Error(detail || 'Login failed');
      }

      setAttemptsRemaining(null);
      setLockoutMsg(null);

      // Iter-109 · 2FA-enabled accounts get a challenge step before a session.
      if (data.requires_2fa) {
        setTwoFA({ pending_token: data.pending_token });
        setTwoFACode('');
        return;
      }

      finishLogin(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login/2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pending_token: twoFA.pending_token, code: twoFACode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || '2FA verification failed');
        if (response.status === 429 || /expired/i.test(msg)) setTwoFA(null);
        throw new Error(msg);
      }
      finishLogin(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative bg-cover bg-center"
      style={staffMode ? { background: 'radial-gradient(120% 60% at 50% 0%, #050505 0%, #0b0b0b 60%)' } : {
        backgroundImage:
          "linear-gradient(rgba(45,45,45,0.55), rgba(45,45,45,0.65)), url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/fc6q13kd_90226.png')",
      }}
      data-testid="login-page"
      data-staff-mode={staffMode ? 'true' : 'false'}
    >
      <Card className={`w-full max-w-md p-8 shadow-2xl ${staffMode ? 'bg-transparent border-0 shadow-none' : 'bg-black/80 backdrop-blur-md border border-white/10'}`} data-testid="login-form">
        {staffMode ? (
          <div className="text-center mb-6" data-testid="staff-login-lockup">
            <div className="flex items-center justify-center gap-2">
              <span className="h-px w-6 sm:w-10 bg-[#C9A962]" />
              <span className="text-2xl sm:text-3xl tracking-[0.35em] text-white font-light">NOBLE</span>
              <span className="h-20 w-20 overflow-hidden rounded-full flex items-center justify-center shrink-0"><img src="/staff-icon-192.png" alt="NECS Field Staff" className="h-20 w-20 object-cover scale-[1.18]" data-testid="staff-login-badge" /></span>
              <span className="text-2xl sm:text-3xl tracking-[0.35em] text-white font-light">EMPIRE</span>
              <span className="h-px w-6 sm:w-10 bg-[#C9A962]" />
            </div>
            <p className="text-base sm:text-lg tracking-[0.45em] text-white mt-1">CLEANING SERVICES</p>
            <p className="text-xs tracking-[0.3em] mt-1 flex items-center justify-center gap-2 text-[#C9A962] whitespace-nowrap"><span className="h-px w-4 bg-[#C9A962]" />SERVICES YOU CAN TRUST<span className="h-px w-6 bg-[#C9A962]" /></p>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none mt-5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="staff-login-title">NECS <span className="text-[#C9A962]">Field Staff</span></h2>
            <div className="mx-auto mt-3 mb-3 h-px w-3/4" style={{ background: 'linear-gradient(90deg, transparent, #C9A962, transparent)' }} />
            <p className="text-base text-zinc-400 px-2">Job cards, QR clock-in and live service updates for Valet &amp; Cleaners teams.</p>
          </div>
        ) : (<>
        <div className="flex justify-center mb-6" data-testid="login-page-logo-wrap">
          <NECSLogo variant="header" size="md" align="center" dataTestId="login-page-logo" />
        </div>

        <h2 className="text-3xl font-bold text-center mb-2 text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }} data-testid="login-page-title">Welcome</h2>
        <p className="text-center text-sm text-zinc-300 mb-6">
          Log in to continue to your Noble Empire account.
        </p>
        </>)}

        {twoFA ? (
          <form onSubmit={handleTwoFASubmit} className="space-y-4" data-testid="twofa-challenge-form">
            <div
              className="p-3 rounded-lg text-xs leading-relaxed"
              style={{ backgroundColor: 'rgba(201,169,98,0.12)', border: '1px solid #C9A962', color: '#e6e6e6' }}
              data-testid="twofa-challenge-notice"
            >
              <p className="font-bold text-[#FFD60A] mb-1">Two-factor authentication</p>
              Enter the 6-digit code from your authenticator app — or one of your one-time backup codes (XXXX-XXXX).
            </div>
            <div>
              <Label htmlFor="twofa-code" className="text-white font-semibold">Authentication code</Label>
              <Input
                id="twofa-code"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)}
                placeholder="123456 or XXXX-XXXX"
                autoFocus
                required
                data-testid="twofa-code-input"
                className="bg-transparent border-white/30 text-white placeholder:text-zinc-400 focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
              disabled={loading || !twoFACode.trim()}
              data-testid="twofa-submit-button"
            >
              {loading ? 'Verifying…' : 'Verify & Sign In'}
            </Button>
            <button
              type="button"
              onClick={() => { setTwoFA(null); setTwoFACode(''); }}
              className="w-full text-center text-sm text-zinc-300 hover:text-[#FFD60A] underline"
              data-testid="twofa-back-button"
            >
              ← Back to login
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-white font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              data-testid="email-input"
              className="bg-transparent border-white/30 text-white placeholder:text-zinc-400 focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-white font-semibold">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                data-testid="password-input"
                className="bg-transparent border-white/30 text-white placeholder:text-zinc-400 focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-zinc-300 hover:text-[#FFD60A] transition"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                data-testid="password-visibility-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Iter-109 · Auth rate-limit meter — warns when 2 attempts away
              from a 15-minute lockout, and shows the lockout notice. */}
          {attemptsRemaining !== null && attemptsRemaining > 0 && attemptsRemaining <= 2 && !lockoutMsg && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: 'rgba(251,191,36,0.14)', border: '1px solid #fbbf24', color: '#fbbf24' }}
              data-testid="login-attempts-warning-chip"
            >
              ⚠ {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before a 15-minute lockout
            </div>
          )}
          {lockoutMsg && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: 'rgba(239,68,68,0.14)', border: '1px solid #ef4444', color: '#fca5a5' }}
              data-testid="login-lockout-chip"
            >
              🔒 {lockoutMsg}
            </div>
          )}

          <Button 
            type="submit" 
            className={staffMode ? 'w-full rounded-full py-6 font-black uppercase tracking-[0.15em] text-base text-[#151515] shadow-[0_0_30px_rgba(201,169,98,0.25)] hover:opacity-90' : 'w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold'}
            style={staffMode ? { background: 'linear-gradient(180deg,#E3C983 0%,#C9A962 100%)' } : undefined}
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? 'Logging in...' : staffMode ? 'Sign In →' : 'Login'}
          </Button>

          {(biometricsAvailable() || staffMode) && (
            <Button
              type="button"
              variant="outline"
              onClick={biometricsAvailable() ? handleBiometricLogin : () => toast.error('Passkeys are not supported on this browser. Use Safari, Chrome or the installed app.')}
              disabled={loading}
              className={`w-full border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10 font-semibold flex items-center justify-center gap-2 ${staffMode ? 'rounded-full py-5 uppercase tracking-[0.1em] text-xs sm:text-sm' : ''}`}
              data-testid="login-biometric-button"
            >
              <Fingerprint className="h-4 w-4" />
              {staffMode ? 'Passkey · Face ID / Fingerprint' : 'Sign in with Fingerprint / FaceID'}
            </Button>
          )}

          <div className="text-center pt-1">
            <Link
              to="/forgot-password"
              className="text-sm text-zinc-300 hover:text-[#FFD60A] hover:underline transition"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </form>
        )}

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/15"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-2 text-zinc-300 ${staffMode ? 'bg-[#0b0b0b] uppercase tracking-[0.25em] text-xs' : 'bg-black'}`}>Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white"
          onClick={handleGoogleLogin}
          data-testid="google-login-button"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>
        {staffMode && (
          <Button type="button" variant="outline" onClick={handleAppleLogin} disabled={loading}
            className="w-full mt-3 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white"
            data-testid="apple-login-button" data-configured={apple.configured ? 'true' : 'false'}>
            <AppleGlyph className="w-5 h-5 mr-2" /> Continue with Apple
          </Button>
        )}

        {isEmbeddedWebView && (
          <div
            className="mt-3 p-3 rounded-lg border text-xs leading-relaxed"
            style={{ backgroundColor: '#FFFCEC', borderColor: '#FFD60A', color: '#3D3D3D' }}
            data-testid="embedded-webview-notice"
          >
            <p className="font-bold mb-1">Heads up — Google may block sign-in here.</p>
            <p>
              You appear to be inside an in-app browser. Google's security policy blocks OAuth in
              embedded webviews (error <code>disallowed_useragent</code>). Tap the share / "Open in browser"
              button to launch this page in Safari or Chrome, then try Google sign-in again — or use
              email & password above.
            </p>
          </div>
        )}

        <div className="text-center text-sm text-zinc-300 mt-6 relative" data-testid="register-cluster">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => setRegisterMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[#FFD60A] hover:underline font-semibold"
            aria-haspopup="menu"
            aria-expanded={registerMenuOpen}
            data-testid="register-link"
          >
            Register here
            <ChevronDown className="w-3.5 h-3.5" />
          </button>

          {registerMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setRegisterMenuOpen(false)}
                data-testid="register-menu-backdrop"
              />
              <div
                role="menu"
                className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 rounded-xl shadow-2xl border-2 overflow-hidden z-50 text-left"
                style={{ backgroundColor: '#0a0a0a', borderColor: '#FFD60A' }}
                data-testid="register-menu"
              >
                <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#FFD60A] border-b border-white/10">
                  Select account type
                </div>
                {[
                  { label: 'Property Admin', role: 'property_admin', path: '/register-property' },
                  { label: 'Tenant / Resident', role: 'tenant_resident' },
                  { label: 'NECS Staff', role: 'necs_staff' },
                  { label: 'NECS Admin', role: 'necs_admin' },
                ].map((opt) => (
                  <button
                    key={opt.role}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setRegisterMenuOpen(false);
                      navigate(opt.path || `/register?role=${opt.role}`);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-white hover:bg-white/5 transition border-b border-white/5 last:border-b-0"
                    data-testid={`register-option-${opt.role}`}
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-[#FFD60A] flex-shrink-0" />
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-sm mt-4">
          <Link to="/" className="text-[#FFD60A] hover:underline font-semibold" data-testid="back-home-link">
            ← Back to Home
          </Link>
        </p>
      </Card>
    </div>
  );
}